import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { 
  Moon, 
  Plus, 
  BarChart3, 
  Lightbulb,
  Mic,
  Square,
  Play,
  Pause,
  Eye,
  Heart,
  Brain,
  Sparkles,
  Calendar,
  Tag,
  Star,
  ArrowRight,
  Search,
  Filter,
  BookOpen,
  Users
} from 'lucide-react';


const Dream = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('log');
  const [dreams, setDreams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(() => {
    // Try to restore insights from localStorage
    const savedInsights = localStorage.getItem('dreamInsights');
    const savedLoaded = localStorage.getItem('dreamInsightsLoaded');
    console.log('Component mount - savedInsights:', !!savedInsights, 'savedLoaded:', savedLoaded);
    
    // If we have saved insights, return them
    if (savedInsights) {
      try {
        return JSON.parse(savedInsights);
      } catch (error) {
        console.error('Error parsing saved insights:', error);
        // Clear corrupted data
        localStorage.removeItem('dreamInsights');
        localStorage.removeItem('dreamInsightsLoaded');
        return null;
      }
    }
    
    // If we don't have insights but have loaded state, clear the loaded state
    if (!savedInsights && savedLoaded === 'true') {
      console.log('Clearing invalid loaded state - no insights data found');
      localStorage.removeItem('dreamInsightsLoaded');
    }
    
    return null;
  });
  const [stats, setStats] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({});
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [regeneratingInsights, setRegeneratingInsights] = useState(false);
  const [insightsLoaded, setInsightsLoaded] = useState(() => {
    // Check if insights were previously loaded from localStorage
    const saved = localStorage.getItem('dreamInsightsLoaded');
    return saved === 'true';
  });
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const recordingIntervalRef = useRef(null);
  const recognitionRef = useRef(null);

  // Dream logging form state
  const [dreamForm, setDreamForm] = useState({
    title: '',
    content: '',
    dreamDate: new Date().toISOString().split('T')[0],
    vividness: 5,
    lucidity: 1,
    emotionBeforeSleep: 'neutral',
    emotionAfterWaking: 'neutral',
    dreamType: 'normal',
    tags: []
  });

  const emotionOptions = {
    beforeSleep: ['calm', 'anxious', 'excited', 'sad', 'angry', 'neutral', 'other'],
    afterWaking: ['calm', 'anxious', 'excited', 'sad', 'angry', 'confused', 'refreshed', 'happy', 'neutral', 'other']
  };

  const dreamTypes = ['normal', 'nightmare', 'lucid', 'recurring', 'prophetic', 'daydream', 'other'];

  useEffect(() => {
    fetchDreams();
    fetchStats();
    // Only fetch insights if we don't have insights data
    console.log('useEffect - insightsLoaded:', insightsLoaded, 'insights:', !!insights);
    if (!insights) {
      console.log('Fetching insights because no data exists');
      fetchInsights();
    } else {
      console.log('Insights already exist, not fetching');
      setInsightsLoaded(true);
    }
  }, []);

  const fetchDreams = async () => {
    setLoading(true);
    try {
      const response = await api.get('/dreams');
      setDreams(response.data.dreams || []);
    } catch (error) {
      console.error('Error fetching dreams:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchInsights = async (isRegenerating = false) => {
    console.log('fetchInsights called - isRegenerating:', isRegenerating);
    if (isRegenerating) {
      setRegeneratingInsights(true);
    } else {
      setInsightsLoading(true);
    }
    
    try {
      console.log('Making API call to fetch insights...');
      const response = await api.get('/dreams/insights/patterns');
      console.log('Insights API response:', response.data);
      setInsights(response.data);
      setInsightsLoaded(true);
      localStorage.setItem('dreamInsightsLoaded', 'true');
      localStorage.setItem('dreamInsights', JSON.stringify(response.data));
      console.log('Insights saved to localStorage');
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      if (isRegenerating) {
        setRegeneratingInsights(false);
      } else {
        setInsightsLoading(false);
      }
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/dreams/stats/overview');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleRegenerateInsights = async () => {
    // Clear the insights loaded state when regenerating
    setInsightsLoaded(false);
    localStorage.removeItem('dreamInsightsLoaded');
    localStorage.removeItem('dreamInsights');
    await fetchInsights(true);
  };

  const clearInsightsCache = () => {
    console.log('Clearing insights cache');
    setInsights(null);
    setInsightsLoaded(false);
    localStorage.removeItem('dreamInsightsLoaded');
    localStorage.removeItem('dreamInsights');
  };

  const handleDreamSubmit = async (e) => {
    e.preventDefault();
    
    // Validate content
    if (!dreamForm.content || dreamForm.content.trim().length === 0) {
      alert('Please enter dream content before submitting.');
      return;
    }
    
    setLoading(true);
    
    console.log('=== DREAM SUBMISSION DEBUG ===');
    console.log('Dream form data:', dreamForm);
    console.log('Content length:', dreamForm.content?.length);
    
    try {
      console.log('Making API request to:', `${api.defaults.baseURL}/dreams`);
      const response = await api.post('/dreams', dreamForm);
      console.log('Dream submission response:', response);

      if (response.status === 200 || response.status === 201) {
        const data = response.data;
        console.log('Dream created successfully:', data);
        setDreams([data.dream, ...dreams]);
        setDreamForm({
          title: '',
          content: '',
          dreamDate: new Date().toISOString().split('T')[0],
          vividness: 5,
          lucidity: 1,
          emotionBeforeSleep: 'neutral',
          emotionAfterWaking: 'neutral',
          dreamType: 'normal',
          tags: []
        });
        // Reset insights loaded state and fetch new insights after logging a dream
        setInsightsLoaded(false);
        localStorage.removeItem('dreamInsightsLoaded');
        localStorage.removeItem('dreamInsights');
        await fetchInsights();
        fetchStats();
        
        // Show success message
        setShowSuccessMessage(true);
        setTimeout(() => {
          setShowSuccessMessage(false);
        }, 5000); // Hide after 5 seconds
      }
    } catch (error) {
      console.error('Error logging dream:', error);
      console.error('Error details:', error.response?.data);
      console.error('Error status:', error.response?.status);
      console.error('Error message:', error.message);
      console.error('Error code:', error.code);
      console.error('Error config:', error.config);
      
      // Show user-friendly error message
      if (error.code === 'ECONNREFUSED' || error.code === 'ERR_NETWORK') {
        alert('Unable to connect to the server. Please check your internet connection and try again.');
      } else if (error.response?.status === 401) {
        alert('Authentication error. Please log in again.');
      } else if (error.response?.status === 400) {
        alert(`Error: ${error.response.data?.error || 'Invalid request'}`);
      } else {
        alert('An error occurred while logging your dream. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const startRecording = async () => {
    try {
      // Check if browser supports speech recognition
      if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
        alert('Speech recognition is not supported in this browser. Please use Chrome or Edge.');
        return;
      }

      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;
      
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      let accumulatedTranscript = '';

      recognition.onresult = (event) => {
        let interimTranscript = '';
        
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const transcript = event.results[i][0].transcript;
          if (event.results[i].isFinal) {
            accumulatedTranscript += transcript + ' ';
          } else {
            interimTranscript += transcript;
          }
        }

        // Update the form with accumulated transcript plus current interim
        setDreamForm(prev => ({ 
          ...prev, 
          content: accumulatedTranscript + interimTranscript 
        }));
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsRecording(false);
        clearInterval(recordingIntervalRef.current);
      };

      recognition.onend = () => {
        setIsRecording(false);
        clearInterval(recordingIntervalRef.current);
      };

      // Only clear content if starting fresh (not if continuing)
      if (!isRecording) {
        setDreamForm(prev => ({ ...prev, content: '' }));
        accumulatedTranscript = '';
      }
      recognition.start();
      setIsRecording(true);
      setRecordingTime(0);
      
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (isRecording && recognitionRef.current) {
      recognitionRef.current.stop();
      setIsRecording(false);
      clearInterval(recordingIntervalRef.current);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getEmotionColor = (emotion) => {
    const colors = {
      calm: 'text-blue-600 bg-blue-50',
      anxious: 'text-yellow-600 bg-yellow-50',
      excited: 'text-green-600 bg-green-50',
      sad: 'text-gray-600 bg-gray-50',
      angry: 'text-red-600 bg-red-50',
      confused: 'text-purple-600 bg-purple-50',
      refreshed: 'text-emerald-600 bg-emerald-50',
      neutral: 'text-gray-600 bg-gray-50',
      other: 'text-gray-600 bg-gray-50'
    };
    return colors[emotion] || colors.neutral;
  };

  const getDreamTypeColor = (type) => {
    const colors = {
      normal: 'text-blue-600 bg-blue-50',
      nightmare: 'text-red-600 bg-red-50',
      lucid: 'text-purple-600 bg-purple-50',
      recurring: 'text-orange-600 bg-orange-50',
      prophetic: 'text-indigo-600 bg-indigo-50',
      daydream: 'text-green-600 bg-green-50',
      other: 'text-gray-600 bg-gray-50'
    };
    return colors[type] || colors.normal;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-sm border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Moon className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Dream Companion</h1>
                <p className="text-sm text-gray-600">Explore your subconscious mind</p>
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white/60 backdrop-blur-sm border-b border-purple-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8">
            {[
              { id: 'log', label: 'Log Dream', icon: Plus },
              { id: 'insights', label: 'Insights', icon: Lightbulb },
              { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
              { id: 'journal', label: 'Dream Journal', icon: BookOpen }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
                {tab.id === 'insights' && (insightsLoading || regeneratingInsights) && (
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-purple-600"></div>
                )}
              </button>
            ))}
          </nav>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dream Logging Tab */}
        {activeTab === 'log' && (
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                  <Moon className="h-8 w-8 text-purple-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-2">Log Your Dream</h2>
                <p className="text-gray-600">Capture your dream while it's fresh in your mind</p>
              </div>

              <form onSubmit={handleDreamSubmit} className="space-y-6">
                {/* Quick Log Mode */}
                <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Quick Log</h3>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={isRecording ? stopRecording : startRecording}
                        className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
                          isRecording 
                            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                            : 'bg-purple-100 text-purple-600 hover:bg-purple-200'
                        }`}
                      >
                        {isRecording ? (
                          <>
                            <Square className="h-4 w-4" />
                            <span>Stop Recording ({formatTime(recordingTime)})</span>
                          </>
                        ) : (
                          <>
                            <Mic className="h-4 w-4" />
                            <span>Voice Record</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <textarea
                    value={dreamForm.content}
                    onChange={(e) => setDreamForm(prev => ({ ...prev, content: e.target.value }))}
                    placeholder="Describe your dream here... What did you see, feel, experience? Don't worry about perfect grammar - just capture the essence while it's fresh."
                    className="w-full h-32 p-4 border border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    required
                  />
                </div>

                {/* Detailed Log Mode */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dream Title (Optional)
                    </label>
                    <input
                      type="text"
                      value={dreamForm.title}
                      onChange={(e) => setDreamForm(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="Give your dream a title..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dream Date
                    </label>
                    <input
                      type="date"
                      value={dreamForm.dreamDate}
                      onChange={(e) => setDreamForm(prev => ({ ...prev, dreamDate: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Vividness (1-10)
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={dreamForm.vividness}
                        onChange={(e) => setDreamForm(prev => ({ ...prev, vividness: parseInt(e.target.value) }))}
                        className="flex-1"
                      />
                      <span className="text-sm font-medium text-gray-600 w-8">{dreamForm.vividness}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Lucidity (1-10)
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="range"
                        min="1"
                        max="10"
                        value={dreamForm.lucidity}
                        onChange={(e) => setDreamForm(prev => ({ ...prev, lucidity: parseInt(e.target.value) }))}
                        className="flex-1"
                      />
                      <span className="text-sm font-medium text-gray-600 w-8">{dreamForm.lucidity}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emotion Before Sleep
                    </label>
                    <select
                      value={dreamForm.emotionBeforeSleep}
                      onChange={(e) => setDreamForm(prev => ({ ...prev, emotionBeforeSleep: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {emotionOptions.beforeSleep.map(emotion => (
                        <option key={emotion} value={emotion}>
                          {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Emotion After Waking
                    </label>
                    <select
                      value={dreamForm.emotionAfterWaking}
                      onChange={(e) => setDreamForm(prev => ({ ...prev, emotionAfterWaking: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {emotionOptions.afterWaking.map(emotion => (
                        <option key={emotion} value={emotion}>
                          {emotion.charAt(0).toUpperCase() + emotion.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Dream Type
                    </label>
                    <select
                      value={dreamForm.dreamType}
                      onChange={(e) => setDreamForm(prev => ({ ...prev, dreamType: e.target.value }))}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    >
                      {dreamTypes.map(type => (
                        <option key={type} value={type}>
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tags (Optional)
                    </label>
                    <input
                      type="text"
                      placeholder="Add tags separated by commas..."
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const tags = e.target.value.split(',').map(tag => tag.trim()).filter(tag => tag);
                          setDreamForm(prev => ({ ...prev, tags: [...prev.tags, ...tags] }));
                          e.target.value = '';
                        }
                      }}
                    />
                    {dreamForm.tags.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {dreamForm.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                          >
                            {tag}
                            <button
                              type="button"
                              onClick={() => setDreamForm(prev => ({
                                ...prev,
                                tags: prev.tags.filter((_, i) => i !== index)
                              }))}
                              className="ml-1 text-purple-600 hover:text-purple-800"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex justify-center pt-6">
                  <button
                    type="submit"
                    disabled={loading || !dreamForm.content.trim()}
                    className="flex items-center space-x-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-8 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Analyzing Dream...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-4 w-4" />
                        <span>Log & Analyze Dream</span>
                      </>
                    )}
                  </button>
                </div>
              </form>
              
              {/* Success Message */}
              {showSuccessMessage && (
                <div className="mt-6 bg-green-50 border border-green-200 rounded-xl p-6">
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-green-800">Dream Logged Successfully!</h3>
                      <p className="text-green-700 mt-1">Your dream has been analyzed and saved. Check your dream journal to see the insights.</p>
                    </div>
                    <button
                      onClick={() => setShowSuccessMessage(false)}
                      className="flex-shrink-0 text-green-400 hover:text-green-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Insights Tab */}
        {activeTab === 'insights' && (
          <div className="space-y-8">
            {insightsLoading ? (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Generating Insights...</h3>
                  <p className="text-gray-600">AI is analyzing your dreams to provide personalized insights</p>
                </div>
              </div>
            ) : insights ? (
              <>
                {/* AI Insights */}
                <div className="bg-white rounded-2xl shadow-xl p-8">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <Lightbulb className="h-6 w-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">AI Dream Insights</h2>
                  </div>
                  
                  {insights.insights && insights.insights.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {insights.insights.map((insight, index) => (
                        <div key={index} className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                          <div className="flex items-center space-x-2 mb-3">
                            <div className="p-1 bg-purple-200 rounded">
                              {insight.type === 'pattern' && <BarChart3 className="h-4 w-4 text-purple-600" />}
                              {insight.type === 'symbol' && <Star className="h-4 w-4 text-purple-600" />}
                              {insight.type === 'mood' && <Heart className="h-4 w-4 text-purple-600" />}
                            </div>
                            <span className="text-sm font-medium text-purple-600 uppercase tracking-wide">
                              {insight.type}
                            </span>
                          </div>
                          <h3 className="font-semibold text-gray-900 mb-2">{insight.title}</h3>
                          <p className="text-gray-600 text-sm mb-3">{insight.description}</p>
                          <p className="text-purple-700 text-sm font-medium">{insight.significance}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                                        <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <Lightbulb className="h-8 w-8 text-purple-600" />
                  </div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">No insights yet</h3>
                      <p className="text-gray-600">Log your first dream to start receiving AI insights</p>
                    </div>
                  )}

                  {/* AI Summary Card */}
                  {insights.aiSummary && (
                    <div className="mt-8 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <div className="mb-4">
                        {/* Header */}
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-2 bg-blue-200 rounded-lg">
                            <Brain className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-medium text-blue-600 uppercase tracking-wide">
                              AI Summary
                            </span>
                            {insights.aiSummary.aiGenerated && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                <Sparkles className="w-3 h-3 mr-1" />
                                AI Generated
                              </span>
                            )}
                          </div>
                        </div>
                        
                        {/* Regenerate Button - Mobile Responsive */}
                        <div className="flex justify-end">
                          <button
                            onClick={handleRegenerateInsights}
                            disabled={regeneratingInsights}
                            className="flex items-center space-x-2 bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                          >
                            {regeneratingInsights ? (
                              <>
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                <span>Regenerating...</span>
                              </>
                            ) : (
                              <>
                                <Sparkles className="h-3 w-3" />
                                <span>Regenerate Insights</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                      <div className="prose prose-sm max-w-none">
                        <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                          {insights.aiSummary.summary}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Recurring Symbols */}
                {insights.recurringSymbols && insights.recurringSymbols.length > 0 && (
                  <div className="bg-white rounded-2xl shadow-xl p-8">
                                      <div className="flex items-center space-x-3 mb-6">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <Star className="h-6 w-6 text-indigo-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900">Recurring Symbols</h2>
                  </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {insights.recurringSymbols.slice(0, 6).map((symbol, index) => (
                        <div key={index} className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-6 border border-indigo-200">
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold text-gray-900 capitalize">{symbol._id}</h3>
                            <span className="text-sm font-medium text-indigo-600 bg-indigo-100 px-2 py-1 rounded-full">
                              {symbol.count} times
                            </span>
                          </div>
                          <p className="text-gray-600 text-sm mb-2">
                            {symbol.meanings && symbol.meanings[0]}
                          </p>
                          <div className="text-xs text-indigo-600">
                            Confidence: {Math.round(symbol.avgConfidence * 100)}%
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <Lightbulb className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No insights yet</h3>
                  <p className="text-gray-600">Log your first dream to start receiving AI insights</p>
                  <button
                    onClick={clearInsightsCache}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                  >
                    Clear Cache & Refresh
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && (
          <div className="space-y-8">
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Total Dreams</p>
                      <p className="text-3xl font-bold text-gray-900">{stats.totalDreams}</p>
                    </div>
                    <div className="p-3 bg-purple-100 rounded-lg">
                      <Moon className="h-6 w-6 text-purple-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Vividness</p>
                      <p className="text-3xl font-bold text-gray-900">{stats.avgVividness?.toFixed(1) || 'N/A'}</p>
                    </div>
                    <div className="p-3 bg-blue-100 rounded-lg">
                      <Eye className="h-6 w-6 text-blue-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Avg Stress Level</p>
                      <p className="text-3xl font-bold text-gray-900">{stats.avgStressLevel?.toFixed(1) || 'N/A'}</p>
                    </div>
                    <div className="p-3 bg-red-100 rounded-lg">
                      <Brain className="h-6 w-6 text-red-600" />
                    </div>
                  </div>
                </div>

                <div className="bg-white rounded-2xl shadow-xl p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">Most Common Type</p>
                      <p className="text-lg font-bold text-gray-900 capitalize">
                        {stats.dreamTypes?.[0]?._id || 'N/A'}
                      </p>
                    </div>
                    <div className="p-3 bg-green-100 rounded-lg">
                      <BarChart3 className="h-6 w-6 text-green-600" />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Dream Type Distribution */}
            {stats?.dreamTypes && (
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Dream Type Distribution</h3>
                <div className="space-y-4">
                  {stats.dreamTypes.map((type, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-4 h-4 rounded-full ${getDreamTypeColor(type._id).split(' ')[1]}`}></div>
                        <span className="font-medium text-gray-900 capitalize">{type._id}</span>
                      </div>
                      <span className="text-sm text-gray-600">{type.count} dreams</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dream Journal Tab */}
        {activeTab === 'journal' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search dreams..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                    />
                  </div>
                </div>
                <div className="flex space-x-2">
                  <select
                    value={filters.type || ''}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  >
                    <option value="">All Types</option>
                    {dreamTypes.map(type => (
                      <option key={type} value={type}>{type.charAt(0).toUpperCase() + type.slice(1)}</option>
                    ))}
                  </select>
                  <button className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                    <Filter className="h-5 w-5 text-gray-600" />
                  </button>
                </div>
              </div>
            </div>

            {/* Dreams List */}
            <div className="space-y-4">
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading dreams...</p>
                </div>
              ) : dreams.length > 0 ? (
                dreams.map((dream) => (
                  <div key={dream._id} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-shadow">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {dream.title || 'Untitled Dream'}
                          </h3>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDreamTypeColor(dream.dreamType)}`}>
                            {dream.dreamType}
                          </span>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center space-x-1">
                            <Calendar className="h-4 w-4" />
                            <span>{new Date(dream.dreamDate).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Eye className="h-4 w-4" />
                            <span>Vividness: {dream.vividness}/10</span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Star className="h-4 w-4" />
                            <span>Lucidity: {dream.lucidity}/10</span>
                          </div>
                        </div>
                        <p className="text-gray-700 line-clamp-3">{dream.content}</p>
                      </div>
                      <button 
                        onClick={() => navigate(`/app/dream/${dream._id}`)}
                        className="p-2 text-gray-400 hover:text-gray-600"
                      >
                        <ArrowRight className="h-5 w-5" />
                      </button>
                    </div>
                    
                    {/* AI Insight Card */}
                    {dream.aiAnalysis && (
                      <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg border border-purple-200">
                        <div className="flex items-center space-x-2 mb-3">
                          <div className="p-1 bg-purple-200 rounded">
                            <Lightbulb className="h-4 w-4 text-purple-600" />
                          </div>
                          <span className="text-sm font-medium text-purple-600">AI Insight</span>
                        </div>
                        
                        <div className="space-y-3">
                          {/* Overall Mood */}
                          {dream.aiAnalysis.overallMood && (
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-gray-600">Mood:</span>
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                dream.aiAnalysis.overallMood === 'positive' ? 'bg-green-100 text-green-800' :
                                dream.aiAnalysis.overallMood === 'negative' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {dream.aiAnalysis.overallMood}
                              </span>
                            </div>
                          )}
                          
                          {/* Key Symbol */}
                          {dream.aiAnalysis.symbols && dream.aiAnalysis.symbols.length > 0 && (
                            <div>
                              <span className="text-sm text-gray-600">Key Symbol: </span>
                              <span className="text-sm font-medium text-gray-900 capitalize">
                                {dream.aiAnalysis.symbols[0].symbol}
                              </span>
                              <p className="text-xs text-gray-600 mt-1 line-clamp-2">
                                {dream.aiAnalysis.symbols[0].meaning}
                              </p>
                            </div>
                          )}
                          
                          {/* Key Theme */}
                          {dream.aiAnalysis.themes && dream.aiAnalysis.themes.length > 0 && (
                            <div>
                              <span className="text-sm text-gray-600">Theme: </span>
                              <span className="text-sm font-medium text-gray-900">
                                {dream.aiAnalysis.themes[0].theme}
                              </span>
                            </div>
                          )}
                          
                          {/* Quick Insight */}
                          {dream.aiAnalysis.insights && dream.aiAnalysis.insights.length > 0 && (
                            <div className="bg-white rounded p-3 border border-purple-200">
                              <p className="text-sm text-gray-700 line-clamp-2">
                                {dream.aiAnalysis.insights[0].insight}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        <button 
                          onClick={() => navigate(`/app/dream/${dream._id}`)}
                          className="mt-3 w-full flex items-center justify-center space-x-2 text-purple-600 hover:text-purple-700 text-sm font-medium"
                        >
                          <span>View Full Analysis</span>
                          <ArrowRight className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                ))
              ) : (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4">
                    <BookOpen className="h-8 w-8 text-purple-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No dreams logged yet</h3>
                  <p className="text-gray-600">Start by logging your first dream to build your dream journal</p>
                </div>
              )}
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default Dream;
